<template>
  <article class="login-card" aria-labelledby="login-card-title">
    <header class="login-card__header">
      <div class="login-card__logo-wrap">
        <LoginBrandLogo
          :is-dark="isDark"
          :org-logo-url="logoUrl"
          :title="title"
          :variant="logoUrl ? 'org' : 'full'"
        />
      </div>

      <h2 id="login-card-title" class="login-card__title">
        Welcome Back
      </h2>

      <p
        v-if="landingLoading"
        class="login-card__status"
      >
        Loading organisation…
      </p>
      <p
        v-else-if="landingError"
        class="login-card__status login-card__status--error"
      >
        {{ landingError }}
      </p>
      <p
        v-else-if="otpSent"
        class="login-card__subtitle"
      >
        Enter the verification code sent to your email.
      </p>
      <p
        v-else
        class="login-card__subtitle"
      >
        Access your AI call agent dashboard
      </p>
    </header>

    <Transition name="login-fade" mode="out-in">
      <LoginEmailStep
        v-if="!otpSent"
        key="email"
        ref="emailStepRef"
        :email="email"
        :rules="emailRules"
        :disabled="formDisabled"
        :loading="loading"
        @update:email="$emit('update:email', $event)"
        @submit="$emit('send-otp')"
      />
      <LoginOtpStep
        v-else
        key="otp"
        :digits="otpDigits"
        :masked-email="maskedEmail"
        :timer-display="timerDisplay"
        :timer-ring-offset="timerRingOffset"
        :timer-urgent="timerUrgent"
        :is-complete="isOtpComplete"
        :disabled="formDisabled"
        :loading="loading"
        :resend-disabled="resendDisabled"
        @digit-input="(index, e) => $emit('otp-input', index, e)"
        @verify="$emit('verify-otp')"
        @resend="$emit('send-otp')"
        @change-email="$emit('change-email')"
        @keydown="(index, e) => $emit('otp-keydown', index, e)"
        @paste="(index, e) => $emit('otp-paste', index, e)"
        @register-refs="$emit('register-otp-refs', $event)"
      />
    </Transition>

    <footer class="login-card__footer">
      <v-icon icon="mdi-lock-outline" class="login-card__footer-icon" />
      <span>Encrypted connection · Enterprise grade security</span>
    </footer>
  </article>
</template>

<script setup>
import { ref } from 'vue'
import LoginBrandLogo from '@/components/Login/LoginBrandLogo.vue'
import LoginEmailStep from '@/components/Login/LoginEmailStep.vue'
import LoginOtpStep from '@/components/Login/LoginOtpStep.vue'

defineOptions({ name: 'LoginCard' })

defineProps({
  isDark: {
    type: Boolean,
    default: true,
  },
  logoUrl: {
    type: String,
    default: null,
  },
  title: {
    type: String,
    default: 'Yandle',
  },
  email: {
    type: String,
    default: '',
  },
  emailRules: {
    type: Array,
    default: () => [],
  },
  otpDigits: {
    type: Array,
    default: () => ['', '', '', '', '', ''],
  },
  otpSent: {
    type: Boolean,
    default: false,
  },
  maskedEmail: {
    type: String,
    default: '',
  },
  timerDisplay: {
    type: String,
    default: '0:00',
  },
  timerRingOffset: {
    type: Number,
    default: 0,
  },
  timerUrgent: {
    type: Boolean,
    default: false,
  },
  isOtpComplete: {
    type: Boolean,
    default: false,
  },
  formDisabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  resendDisabled: {
    type: Boolean,
    default: false,
  },
  landingLoading: {
    type: Boolean,
    default: false,
  },
  landingError: {
    type: String,
    default: '',
  },
})

defineEmits([
  'update:email',
  'send-otp',
  'verify-otp',
  'change-email',
  'otp-input',
  'otp-keydown',
  'otp-paste',
  'register-otp-refs',
])

const emailStepRef = ref(null)

defineExpose({
  get formRef() {
    return emailStepRef.value?.formRef
  },
})
</script>
